/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable react/jsx-no-target-blank */
import React, { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import banner from 'assets/mentiway-banner.svg';
import { border, colors } from 'theme';
import { usePanel } from 'contexts/panelContext';
import { Box, BoxSection } from 'components/_new/Box';
import { Spinner } from 'components/_new/Spinner';
import { translatedStaticResourceResolver, getBlogUrl } from 'utils/translatedStaticResourceResolver';
import dayjs from 'dayjs';
import { dateFormat } from 'utils/dateFormat';
import { Divider } from 'components/_new/Divider';
import { TimelineEventItemTypeEnum } from 'types/Timeline';
import { Button } from 'components/_new/Button';
import { Dialog } from 'components/_new/Dialog';
import { IconSvg } from 'components/_new/Icon/IconSvg';
import { TodosResponse } from 'types/Todo';
import TodoItem from './TodoItem';
import { useAuth } from '../../contexts/authContext';
import { useTimelineSimple } from '../_common/timeline/useTimeline';
import { TimelineEventDetailsDialog } from '../_common/timeline/TimelineEventDetailsDialog';
import { TimelineList } from '../_common/timeline/TimelineList';

const TimelineSection = () => {
	const { t } = useTranslation();

	const { data, refetch, isLoading } = useTimelineSimple();

	const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
	const selectedEvent = selectedEventId
		? data?.find(({ eventId }) => eventId !== null && eventId === selectedEventId) || null
		: null;

	const handleEventClick = (key: string) => {
		if (data) {
			const timelineEvent = data.find((event) => event.key === key)!;
			if (timelineEvent.type === TimelineEventItemTypeEnum.SESSION) {
				//
			} else {
				// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
				setSelectedEventId(timelineEvent.eventId);
			}
		}
	};

	const [showCalendarModalOpen, setShowCalendarModalOpen] = useState(false);

	return (
		<>
			<Dialog
				title={t('timeline.title')}
				visible={showCalendarModalOpen}
				onHide={() => {
					setShowCalendarModalOpen(false);
				}}
				size="lg"
			>
				<TimelineList dialogMode />
			</Dialog>

			<TimelineEventDetailsDialog
				data={selectedEvent!}
				onUpdate={() => {
					refetch();
				}}
				visible={Boolean(selectedEventId) && Boolean(selectedEvent)}
				onHide={() => {
					setSelectedEventId(null);
				}}
			/>

			<Box variant="white-bordered">
				<BoxSection header={t('timeline.events')}>
					{isLoading ? (
						<Spinner center />
					) : (
						<>
							{(data || []).length > 0 && (
								<div className="flex flex-column justify-content-center">
									<div className="w-full">
										{data?.map(
											({ key, type, summary, dayDate, startAt, endAt, session }, index) => {
												const startsHour = startAt ? dayjs(startAt).format('HH:mm') : null;
												const endsHour = endAt ? dayjs(endAt).format('HH:mm') : null;
												const dateOnly = dateFormat(dayDate, 'date');
												const externalLink = session?.link;

												return (
													<Fragment key={key}>
														<Link
															to={externalLink || '#'}
															className="flex flex-column gap-1 justify-content-start"
															onClick={(event) => {
																if (!externalLink) {
																	handleEventClick(key);
																	event.preventDefault();
																}
															}}
														>
															<div className="mb-1">{summary}</div>

															<div className="flex flex-row align-items-center gap-2">
																<IconSvg
																	name="calendar-full"
																	className="icon-container icon-container-sm text-primary"
																/>
																<div className="text-left flex-1 font-bold">
																	{dateOnly || ''}
																</div>
															</div>

															{type !== TimelineEventItemTypeEnum.MILESTONE && (
																<div className="flex flex-row align-items-center gap-2">
																	<IconSvg
																		name="clock"
																		className="icon-container icon-container-sm text-primary"
																	/>
																	<div className="text-left flex-1 font-bold">
																		{startsHour} - {endsHour}
																	</div>
																</div>
															)}
														</Link>
														<Divider margin={3} />
													</Fragment>
												);
											}
										)}
									</div>
									<Button
										label={t('timeline.showFullCalendar')}
										variant="primary-text"
										variantSize="sm"
										onClick={() => {
											setShowCalendarModalOpen(true);
										}}
									/>
								</div>
							)}
							{(data || []).length === 0 && (
								<div className="text-muted">{t('timeline.noUpcomingEvents')}</div>
							)}
						</>
					)}
				</BoxSection>
			</Box>
		</>
	);
};

const Tools = () => {
	const { t } = useTranslation();
	const { todosData, currentProgram } = usePanel();
	const timelineEnabled = Boolean(currentProgram?.eventsEnabled);

	// TODS TEST DATA ALL TYPES
	// const todosData: TodosResponse = {
	// 	common: [
	// 		{ type: 'application_during_deletion', variables: {} },
	// 		{ type: 'read_guides', variables: {} },
	// 		{ type: 'no_accepted_pair', variables: {} },
	// 		{ type: 'wait_for_mentee', variables: {} },
	// 		{ type: 'li_label_info', variables: {} },
	// 	],
	// 	pair: [
	// 		{
	// 			name: 'Some person',
	// 			items: [
	// 				{ type: 'pair_during_deletion', variables: {} },
	// 				{
	// 					type: 'confirm_entry_meeting',
	// 					variables: {
	// 						pairId: 123,
	// 					},
	// 				},
	// 				{
	// 					type: 'arrange_first_session_date',
	// 					variables: {
	// 						sessionId: 123,
	// 						sessionNumber: 123,
	// 						sessionDate: new Date().toLocaleString(),
	// 						pairId: 123,
	// 					},
	// 				},
	// 				{
	// 					type: 'contract_initial_sign_required',
	// 					variables: {
	// 						pairId: 123,
	// 						name: 'Jan Kowalski',
	// 					},
	// 				},
	// 				{
	// 					type: 'contract_goals_sign_required',
	// 					variables: {
	// 						pairId: 123,
	// 						name: 'Jan Kowalski',
	// 					},
	// 				},
	// 				{
	// 					type: 'final_report_mentee',
	// 					variables: {
	// 						pairId: 123,
	// 					},
	// 				},
	// 				{
	// 					type: 'final_report_mentor',
	// 					variables: {
	// 						pairId: 123,
	// 					},
	// 				},
	// 				{
	// 					type: 'evaluation_survey_required',
	// 					variables: {
	// 						pairId: 123,
	// 						sessionId: 123,
	// 					},
	// 				},
	// 				{
	// 					type: 'session_reminder',
	// 					variables: {
	// 						sessionId: 123,
	// 						sessionNumber: 123,
	// 						sessionDate: new Date().toLocaleString(),
	// 						pairId: 123,
	// 					},
	// 				},
	// 				{
	// 					type: 'add_session_date',
	// 					variables: {
	// 						sessionId: 123,
	// 						sessionNumber: 123,
	// 						sessionDate: new Date().toLocaleString(),
	// 						pairId: 123,
	// 					},
	// 				},
	// 			],
	// 		},
	// 	],
	// };

	return (
		<div className="flex flex-column gap-4 p-3 h-full" style={{ borderLeft: border(colors.lightGray) }}>
			<Box variant="white-bordered">
				<BoxSection header={t('userPanel.todo.header')}>
					{todosData.common.length === 0 && todosData.pair.length === 0 ? (
						<p>{t('userPanel.todo.noNotification')}</p>
					) : (
						<>
							{todosData.common.length > 0 && (
								<ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
									{todosData.common.map(({ key, ...todo }) => (
										<TodoItem {...todo} key={key} />
									))}
								</ul>
							)}

							{todosData.pair.map(
								({ name, items }, i) =>
									items.length > 0 && (
										<div key={name} className="flex flex-column gap-1">
											<div>
												{t('userPanel.todo.pairWith')}: <span className="bold">{name}</span>
											</div>
											<ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
												{items.map(({ key, ...todo }) => (
													<TodoItem {...todo} key={key} />
												))}
											</ul>
										</div>
									)
							)}
						</>
					)}
				</BoxSection>
			</Box>

			{timelineEnabled && <TimelineSection />}

			<div className="mt-auto flex justify-content-between align-items-end w-full relative pt-4 pb-2">
				<div className="relative">
					<p
						style={{
							position: 'absolute',
							top: '-20px',
							fontSize: '8px',
							margin: 0,
							color: colors.darkPurplishBlue,
						}}
					>
						powered by
					</p>
					<a href={getBlogUrl()} target="_blank" rel="noreferrer">
						<img src={banner} alt="banner" className="w-8rem" />
					</a>
				</div>
				<div
					className="flex flex-column align-items-end text-color-secondary gap-1"
					style={{ fontSize: '0.6rem' }}
				>
					<a href={translatedStaticResourceResolver('privacy')} target="_blank">
						{t('userPanel.footer.privacyPolicy')}
					</a>
					<a href={translatedStaticResourceResolver('terms')} target="_blank" rel="noreferrer">
						{t('userPanel.footer.termsAndConditions')}
					</a>
				</div>
			</div>
		</div>
	);
};

export default Tools;
